// Agents vocaux › Numéros — the account's phone numbers: bought here (one
// monthly subscription per number, taken from the numbers Unanimity keeps
// routed to the platform — nobody types a number), the voice agent answering
// each one, cancellation, invoices. Backend: app/api/voice_numbers.py,
// docs/voice-numbers.md.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaExternalLinkAlt, FaPlus } from 'react-icons/fa';
import BuyNumber from './BuyNumber';
import NumberCard from './NumberCard';
import {
    INVOICE_STATUS_LABELS, canManage, confirmCheckout, fail, formatDay, formatPrice, getInvoices, getNumbers, listAgents,
} from './api';
import './VoiceSetup.css';

const POLL_MS = 3000;
const POLL_TRIES = 5;

const Invoices = ({ t, lang }) => {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && rows === null) {
            try {
                const res = await getInvoices();
                setRows(res.data.invoices || []);
            } catch (err) {
                setError(fail(err, t('voiceSetup.invoices.error', 'Les factures sont indisponibles pour le moment.')));
                setRows([]);
            }
        }
    };

    return (
        <section className="vs-card vs-invoices" id="voice-invoices">
            <button type="button" className="vs-disclosure" aria-expanded={open} onClick={toggle}>
                {t('voiceSetup.invoices.title', 'Factures')}
            </button>
            {open && (
                rows === null ? <p className="vs-note">{t('voiceSetup.loading', 'Chargement…')}</p> : (
                    <>
                        {error && <p className="vs-error">{error}</p>}
                        {!error && rows.length === 0 && <p className="vs-note">{t('voiceSetup.invoices.none', 'Aucune facture pour l’instant.')}</p>}
                        <ul className="vs-invoice-list">
                            {rows.map((row) => (
                                <li key={row.id}>
                                    <span>{formatDay(row.created_at, lang)}</span>
                                    <span className="vs-invoice-number">{row.number || '—'}</span>
                                    <span className="vs-invoice-amount">{formatPrice(row.amount_eur, lang)}</span>
                                    <span className={`vs-chip vs-chip--${row.status === 'paid' ? 'success' : row.status === 'open' ? 'danger' : 'neutral'}`}>
                                        {t(`voiceSetup.invoices.status.${row.status}`, INVOICE_STATUS_LABELS[row.status] || row.status || '')}
                                    </span>
                                    {row.hosted_invoice_url && (
                                        <a className="vs-link" href={row.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                                            {row.status === 'open' ? t('voiceSetup.invoices.pay', 'Régler') : t('voiceSetup.invoices.view', 'Voir')} <FaExternalLinkAlt aria-hidden="true" />
                                        </a>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </>
                )
            )}
        </section>
    );
};

const VoiceNumbers = () => {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [view, setView] = useState(null);
    const [agents, setAgents] = useState([]);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState(null);
    const [buying, setBuying] = useState(searchParams.get('buy') === '1');
    const manage = canManage();
    const handledReturn = useRef(false);

    const load = useCallback(async () => {
        try {
            const [numbersRes, agentsRes] = await Promise.all([getNumbers(), listAgents()]);
            setView(numbersRes.data);
            setAgents(agentsRes.data.agents || []);
            setError('');
            return numbersRes.data;
        } catch (err) {
            setError(fail(err, t('voiceSetup.loadError', 'Les numéros n’ont pas pu être chargés. Rechargez la page.')));
            return null;
        }
    }, [t]);

    // Back from Stripe: settle the checkout now (the sweep would within minutes).
    useEffect(() => {
        if (handledReturn.current) return;
        handledReturn.current = true;
        const outcome = searchParams.get('voice_number');
        const sessionId = searchParams.get('session_id');
        const clean = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('voice_number');
            next.delete('session_id');
            next.delete('buy');
            setSearchParams(next, { replace: true });
        };
        if (outcome === 'cancelled') {
            setBanner({ type: 'neutral', text: t('voiceSetup.return.cancelled', 'Paiement annulé : aucun montant n’a été débité.') });
            clean();
        }
        if (outcome !== 'paid' || !sessionId) {
            load();
            return;
        }
        (async () => {
            setBanner({ type: 'info', text: t('voiceSetup.return.checking', 'Paiement reçu, activation du numéro…') });
            let status = null;
            let number = '';
            try {
                const res = await confirmCheckout(sessionId);
                status = res.data.status;
                number = (res.data.numbers || []).find((n) => n.phone_number === res.data.phone_number)?.display || '';
                setView(res.data);
                const agentsRes = await listAgents();
                setAgents(agentsRes.data.agents || []);
            } catch (err) {
                await load();
            }
            for (let i = 0; i < POLL_TRIES && (status === 'pending' || status === 'fulfilling'); i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((resolve) => setTimeout(resolve, POLL_MS));
                // eslint-disable-next-line no-await-in-loop
                const fresh = await load();
                status = fresh?.pending_checkout ? 'pending' : 'active';
            }
            if (status === 'active' || status === 'canceling') {
                setBanner({ type: 'success', text: number
                    ? t('voiceSetup.return.active', 'Votre numéro {{number}} est actif.', { number })
                    : t('voiceSetup.return.activeShort', 'Votre numéro est actif.') });
            } else if (status === 'paid_unassigned') {
                setBanner({ type: 'warning', text: t('voiceSetup.return.unassigned', 'Paiement validé. Votre numéro vous sera attribué dès qu’un nouveau lot est disponible : vous serez notifié.') });
            } else {
                setBanner({ type: 'info', text: t('voiceSetup.return.pending', 'Le paiement est en cours de validation : le numéro apparaîtra ici dans quelques minutes.') });
            }
            clean();
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error && !view) return <div className="vs-shell"><p className="vs-error" role="alert">{error}</p></div>;
    if (!view) return <div className="vs-shell"><p className="vs-note">{t('voiceSetup.loading', 'Chargement…')}</p></div>;

    const numbers = view.numbers || [];
    const offer = view.offer || {};
    const pending = view.pending_checkout;

    return (
        <div className="vs-shell">
        <div className="vs-page">
            <header className="vs-header">
                <div>
                    <h1>{t('voiceSetup.numbers.pageTitle', 'Numéros de téléphone')}</h1>
                    <p>{t('voiceSetup.numbers.pageSubtitle', 'Les numéros que vos clients appellent, et l’agent vocal qui décroche sur chacun.')}</p>
                </div>
            </header>

            {banner && <p className={`vs-banner vs-banner--${banner.type}`} role="status">{banner.text}</p>}
            {error && <p className="vs-error" role="alert">{error}</p>}

            <section className="vs-card">
                <header className="vs-card-header">
                    <div>
                        <h3>{t('voiceSetup.numbers.title', 'Vos numéros')}</h3>
                        <p>{t('voiceSetup.numbers.desc', '{{price}} par numéro et par mois, sans engagement. Un numéro, un agent qui répond.', {
                            price: formatPrice(offer.monthly_price_eur, i18n.language),
                        })}</p>
                    </div>
                    {manage && numbers.length > 0 && !buying && (
                        <button type="button" className="u-btn u-btn--secondary" onClick={() => setBuying(true)}>
                            <FaPlus aria-hidden="true" /> {t('voiceSetup.addNumber', 'Ajouter un numéro')}
                        </button>
                    )}
                </header>

                {!manage && <p className="vs-note">{t('voiceSetup.adminOnly', 'Seuls les administrateurs du compte achètent, routent et résilient les numéros.')}</p>}

                {pending && (
                    <p className="vs-banner vs-banner--info">
                        {pending.status === 'fulfilling'
                            ? t('voiceSetup.pending.fulfilling', 'Paiement validé : activation du {{number}} en cours.', { number: pending.display })
                            : t('voiceSetup.pending.open', 'Paiement en attente pour le {{number}}, réservé pour vous pendant le paiement. Page de paiement fermée ? Recommencez : rien n’a été débité.', { number: pending.display })}
                    </p>
                )}
                {view.paid_unassigned > 0 && (
                    <p className="vs-banner vs-banner--warning">
                        {t('voiceSetup.unassigned', 'Paiement validé : votre numéro vous sera attribué dès qu’un nouveau lot est disponible.')}
                    </p>
                )}

                {buying && (
                    <BuyNumber offer={offer} agents={agents} defaultAgentId={agents[0]?.id}
                        onCancel={() => setBuying(false)} />
                )}

                {numbers.length === 0 && !buying && (
                    <div className="vs-empty">
                        <h4>{t('voiceSetup.empty.title', 'Pas encore de numéro')}</h4>
                        <p>{t('voiceSetup.empty.text', 'Choisissez un numéro et l’agent qui y répond : vos clients l’appellent pour suivre un colis ou ouvrir une demande, l’agent décroche 24 h/24.')}</p>
                        {manage && (
                            <button type="button" className="u-btn u-btn--primary" onClick={() => setBuying(true)}>
                                {t('voiceSetup.empty.cta', 'Choisir un numéro')}
                            </button>
                        )}
                    </div>
                )}

                <div className="vs-number-list">
                    {numbers.map((item) => (
                        <NumberCard key={item.phone_number} item={item} agents={agents} manage={manage}
                            onChange={(data) => { setView(data); setError(''); }} onError={setError} />
                    ))}
                </div>
            </section>

            {manage && view.has_billing && <Invoices t={t} lang={i18n.language} />}
        </div>
        </div>
    );
};

export default VoiceNumbers;
